import PoorVisionMode from '../../components/Main/TerminalModes/PoorVision/PoorVisionMode/PoorVisionMode';
import React, { useEffect, useMemo, useState } from 'react';
import NormalMode from '../../components/Main/TerminalModes/Normal/NormalMode/NormalMode';
import { useNavigate } from 'react-router-dom';
import { useGetOrderUnits } from '../../hooks/TerminalServices/useGetOrderUnits';
import { useGetTerminalAppearance } from '../../hooks/TerminalServices/useGetTerminalAppearance';
import { StaticTokens } from '../../constants/API';
import { ClientLicenseHasExpiredModal } from '../../components/LicenseModals/LicenseHasExpiredModal/ClientLicenseHasExpiredModal';
import { TerminalAppearanceResponse } from '../../types/Admin/TerminalAppearanceTypes';
import { baseSoundURL } from '../../config';

export enum TerminalMode {
  Normal = 'normal',
  NotNormal = 'notNormal'
}

const TickerText = '';

const makeCssImportant = (css: string) => {
  if (!css) return '';
  return css.replace(/:([^;{}]+);/g, (match, value) => {
    if (value.includes('!important')) {
      return match;
    }
    return `:${value.trim()} !important;`;
  });
};

const normalizeMediaUrl = (url: string) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url) || url.startsWith('//')) {
    return url;
  }
  const separator = url.startsWith('/') ? '' : '/';
  return `${baseSoundURL}${separator}${url}`;
};

const normalizeButtonImageUrl = (url: string | null | undefined) => {
  if (!url) return url;
  return normalizeMediaUrl(url);
};

type TerminalPreviewMessage =
  | {
      type: 'TERMINAL_PREVIEW_APPEARANCE';
      payload: { appearance: TerminalAppearanceResponse };
    }
  | { type: string; payload?: any };

const TerminalPage = () => {
  const [mode, setMode] = useState<TerminalMode>(TerminalMode.Normal);
  const { orderUnitsData, isLoadingOrderUnitsData, isOrderUnitsDataReceived } = useGetOrderUnits();
  const isPreviewMode =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('preview') === '1'
      : false;
  const branchQueueIdFromUrl =
    typeof window !== 'undefined'
      ? (() => {
          const raw = new URLSearchParams(window.location.search).get('branchQueueId');
          if (!raw) return null;
          const parsed = Number(raw);
          return Number.isFinite(parsed) ? parsed : null;
        })()
      : null;
  const { terminalAppearanceResponse, isLoadingTerminalAppearance, isTerminalAppearanceReceived } =
    useGetTerminalAppearance(branchQueueIdFromUrl);
  const [previewAppearance, setPreviewAppearance] = useState<TerminalAppearanceResponse | null>(null);
  const navigate = useNavigate();
  useEffect(() => {
    const currentToken =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('token')
        : null;
    if (isPreviewMode) return;
    if (!currentToken) {
      navigate(`/terminal/?token=${StaticTokens.terminalToken}`);
    }
  }, [isPreviewMode, navigate]);

  useEffect(() => {
    if (!isPreviewMode) return;

    const handler = (event: MessageEvent<TerminalPreviewMessage>) => {
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.type !== 'TERMINAL_PREVIEW_APPEARANCE') return;
      const incoming = data.payload?.appearance;
      if (!incoming?.settings) return;

      const normalized: TerminalAppearanceResponse = {
        ...incoming,
        settings: incoming.settings
          ? {
              ...incoming.settings,
              buttons: incoming.settings.buttons
                ? {
                    ...incoming.settings.buttons,
                    ticketOuterCircleSmallUrl: normalizeButtonImageUrl(
                      incoming.settings.buttons.ticketOuterCircleSmallUrl,
                    ),
                  }
                : incoming.settings.buttons,
              sectionButtons: incoming.settings.sectionButtons
                ? {
                    ...incoming.settings.sectionButtons,
                    ticketOuterCircleSmallUrl: normalizeButtonImageUrl(
                      incoming.settings.sectionButtons.ticketOuterCircleSmallUrl,
                    ),
                  }
                : incoming.settings.sectionButtons,
            }
          : incoming.settings,
        backgrounds: (incoming.backgrounds || []).map((background) => ({
          ...background,
          url: normalizeMediaUrl(background.url),
        })),
        childrenBackgrounds: (incoming.childrenBackgrounds || []).map((background) => ({
          ...background,
          url: normalizeMediaUrl(background.url),
        })),
      };

      setPreviewAppearance(normalized);
    };

    window.addEventListener('message', handler as any);
    return () => window.removeEventListener('message', handler as any);
  }, [isPreviewMode]);

  const shouldRenderTerminal =
    !isLoadingTerminalAppearance ||
    isTerminalAppearanceReceived ||
    Boolean(terminalAppearanceResponse?.settings);

  const effectiveAppearance = previewAppearance ?? terminalAppearanceResponse;

  const effectiveCustomCss = useMemo(() => {
    const css = effectiveAppearance?.settings?.customCss;
    return css ? makeCssImportant(css) : '';
  }, [effectiveAppearance?.settings?.customCss]);

  return (
    <>
      {!shouldRenderTerminal && <div></div>}
      {shouldRenderTerminal && mode === TerminalMode.Normal && (
        <NormalMode
          setMode={setMode}
          orderUnitsData={orderUnitsData}
          tickerText={TickerText}
          appearance={effectiveAppearance}
        />
      )}
      {shouldRenderTerminal && mode === TerminalMode.NotNormal && (
        <PoorVisionMode
          setMode={setMode}
          orderUnitsData={orderUnitsData}
          autoReturnHomeIdleSeconds={
            effectiveAppearance?.settings?.autoReturnHome?.idleSeconds ?? 0
          }
          isPreviewMode={isPreviewMode}
        />
      )}

      <ClientLicenseHasExpiredModal />

      {effectiveCustomCss && (
        <style
          dangerouslySetInnerHTML={{
            __html: effectiveCustomCss
          }}
        />
      )}
    </>
  );
};

export default TerminalPage;
